/**
 * whatsappAdmin.service.ts — Admin → Integrations → WhatsApp.
 *
 * One surface: the practice's WhatsApp sender (`aa_accounts_whatsapp`), which is
 * what the comm-log WhatsApp composer sends from.
 *
 * Nothing here is a credential. The Twilio account SID and auth token are
 * reused per account from the existing voice/SMS settings server-side, so no
 * value on this page is masked and none needs to be.
 */
import { apiClient } from './apiClient';

export interface WhatsAppConnection {
  /** The WhatsApp-enabled number, as the practice entered it. */
  senderNumber: string;
  /** Optional messaging service id; used instead of the number when present. */
  messagingServiceSid: string;
  /** The business name patients see. Informational only. */
  displayName: string;
  /** The practice's confirmation that WhatsApp approved this sender. */
  isApproved: boolean;
  /** Usable sender AND approved — the same conditions a send enforces. */
  connected: boolean;
  updatedAt: string | null;
}

export interface WhatsAppConnectionInput {
  senderNumber?: string;
  messagingServiceSid?: string;
  displayName?: string;
  isApproved?: boolean;
}

export function getWhatsAppConnection(): Promise<WhatsAppConnection> {
  return apiClient
    .get<{ data: WhatsAppConnection }>('/whatsapp/connection')
    .then((r) => r.data.data);
}

export function updateWhatsAppConnection(
  payload: WhatsAppConnectionInput,
): Promise<WhatsAppConnection> {
  return apiClient
    .put<{ data: WhatsAppConnection }>('/whatsapp/connection', payload)
    .then((r) => r.data.data);
}

/**
 * Soft-deletes the connection. Past WhatsApp conversations stay in each
 * patient's history; only the ability to send is removed.
 */
export function disconnectWhatsApp(): Promise<WhatsAppConnection> {
  return apiClient
    .delete<{ data: WhatsAppConnection }>('/whatsapp/connection')
    .then((r) => r.data.data);
}

/**
 * Per-check outcome. `unknown` is NOT failure — it means we could not reach a
 * verdict (provider unreachable, credentials rejected before we got that far),
 * and must never be rendered as "your number is wrong".
 */
export type WhatsAppCheckState = 'pass' | 'fail' | 'unknown';

export interface WhatsAppConnectionCheck {
  canSend: boolean;
  credentials: {
    state: WhatsAppCheckState;
    /** False when we fall back to Prospera's shared provider account. */
    usingPracticeAccount: boolean;
  };
  sender: {
    state: WhatsAppCheckState;
    /** Provider sender status when found, e.g. ONLINE, PENDING_VERIFICATION. */
    status: string | null;
    e164: string | null;
  };
  messagingService: { state: WhatsAppCheckState | 'not_set' };
}

/**
 * Asks the provider whether this number is a live WhatsApp sender. Pass
 * `senderNumber` to check a number before saving it; omit to check the saved
 * one. A passing check records the approval flag server-side.
 */
export function testWhatsAppConnection(senderNumber?: string): Promise<WhatsAppConnectionCheck> {
  return apiClient
    .post<{ data: WhatsAppConnectionCheck }>(
      '/whatsapp/connection/test',
      senderNumber ? { senderNumber } : {},
    )
    .then((r) => r.data.data);
}
